'use client'

import { useEffect } from 'react'
import { useOptionalTenant } from '@/contexts/TenantContext'
import { useWmtAppPlatformContext } from '@/contexts/WmtAppPlatformContext'
import { useResolveExternalHref } from '@/lib/wmtAppPlatform/useResolveExternalHref'

/**
 * When Walmart native-app external deeplinks are enabled and the platform cookie
 * is present, rewrite document anchors and `window.open` targets so exits leave
 * the WMT webview via the native deeplink.
 */
export function ExternalNavigationBridge() {
    const resolveExternalHref = useResolveExternalHref()
    const tenant = useOptionalTenant()
    const { platform } = useWmtAppPlatformContext()
    const enabled = tenant?.capabilities?.nativeAppExternalDeeplinks === true && platform !== null

    useEffect(() => {
        if (!enabled) return

        const rewriteAnchor = (anchor: HTMLAnchorElement) => {
            const href = anchor.getAttribute('href')
            if (!href) return
            const openExternally = anchor.target === '_blank'
            const nextHref = resolveExternalHref(href, { openExternally })
            if (nextHref !== href) {
                anchor.setAttribute('href', nextHref)
            }
        }

        const rewriteTree = (root: ParentNode) => {
            root.querySelectorAll('a[href]').forEach((node) => {
                rewriteAnchor(node as HTMLAnchorElement)
            })
        }

        rewriteTree(document)

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'attributes' && mutation.target instanceof HTMLAnchorElement) {
                    rewriteAnchor(mutation.target)
                    continue
                }
                mutation.addedNodes.forEach((node) => {
                    if (node instanceof HTMLAnchorElement) {
                        rewriteAnchor(node)
                    } else if (node instanceof Element) {
                        rewriteTree(node)
                    }
                })
            }
        })

        observer.observe(document.documentElement, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['href', 'target'],
        })

        const originalOpen = window.open.bind(window)
        window.open = ((url?: string | URL, target?: string, features?: string) => {
            if (typeof url === 'string' && url.length > 0) {
                return originalOpen(resolveExternalHref(url, { openExternally: true }), target, features)
            }
            return originalOpen(url, target, features)
        }) as typeof window.open

        return () => {
            observer.disconnect()
            window.open = originalOpen
        }
    }, [enabled, resolveExternalHref])

    return null
}
